<template>
    <div class="step-content">
        <slot name="title">
            <StepTitle
                :active="active"
                :error="error"
                :complete="complete"
                :title="$t(`email.broadcast.editor.sectionTitle.audience`)"
            />
        </slot>

        <div class="main-content">
            <div v-show="active" class="input-fields">
                <div class="recipients-field">
                    <RecipientAddressField
                        v-model="toAddress"
                        :submitted="submitted"
                        :liquid-merge-fields-in-automations-enabled="liquidMergeFieldsInAutomationsEnabled"
                        data-qa="recipient-address-field"
                        @custom-value-selected="toAddress = $event"
                    />
                </div>

                <div class="action-buttons">
                    <DsOutlineButton
                        class="save"
                        data-qa="save-button"
                        @click="save"
                    >
                        {{ $t('global.save') }}
                    </DsOutlineButton>

                    <DsTextButton
                        class="cancel"
                        @click.stop="cancel"
                    >
                        {{ $t('global.cancel') }}
                    </DsTextButton>
                </div>
            </div>

            <EmailBroadcastStepOverview v-show="!active" :sections="overviewSections" />
        </div>

        <slot name="action" :actionText="$t('email.broadcast.steps.audience.actionText')" />
    </div>
</template>

<script>
import EmailBroadcastStepOverview from './EmailBroadcastStepOverview';
import StepTitle from '@/components/unlayer/UnlayerEmailStepTitle.vue';
import RecipientAddressField from '@/components/unlayer/RecipientAddressField';
import { isSpaApp } from '@/api/shared';
import {
    DEFAULT_RECIPIENT_VALUES as LEGACY_RECIPIENT_VALUES,
    DEFAULT_RECIPIENT_VALUES_LIQUID_SYNTAX as LIQUID_RECIPIENT_VALUES,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import {
    REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT,
    REGEX_CUSTOM_EMAIL_ADDRESS_WITH_LIQUID_MERGE_FIELD_SUPPORT,
} from '@/utils/email.util';

export default {
    components: {
        EmailBroadcastStepOverview,
        RecipientAddressField,
        StepTitle,
    },

    props: {
        active: Boolean,
        error: Boolean,
        complete: Boolean,
        savedToAddress: String,
        liquidMergeFieldsInAutomationsEnabled: Boolean,
    },

    data() {
        const recipientValues = this.liquidMergeFieldsInAutomationsEnabled
            ? LIQUID_RECIPIENT_VALUES
            : LEGACY_RECIPIENT_VALUES;

        return {
            submitted: false,
            hasError: false,
            isSpaApp: true,
            toAddress: recipientValues.EMAIL,
            recipientValues, 
        };
    },

    async created() {
        this.isSpaApp = await isSpaApp();
    },

    watch: {
        savedToAddress: {
            handler() {
                this.syncData();
            },
            immediate: true,
        },
    },

    computed: {
        customEmailAddressRegex() {
            return this.liquidMergeFieldsInAutomationsEnabled
                ? REGEX_CUSTOM_EMAIL_ADDRESS_WITH_LIQUID_MERGE_FIELD_SUPPORT
                : REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT;
        },

        overviewSections() {
            return [{
                content: this.overViewLabel,
                placeholder: this.$t('unlayerBuilder.emailRecipient.label.placeholder'),
                title: this.$t('global.to'),
            }];
        },

        overViewLabel() {
            const isSpa = this.isSpaApp;
            const defaultLabel = isSpa ? this.$t('unlayerBuilder.emailRecipient.label.work') : this.$t('unlayerBuilder.emailRecipient.label.email');

            if (!this.toAddress) {
                return defaultLabel;
            }

            if (this.isDefaultRecipientValue(this.toAddress)) {
                switch (this.toAddress) {
                case LEGACY_RECIPIENT_VALUES.EMAIL:
                case LIQUID_RECIPIENT_VALUES.EMAIL:
                    return defaultLabel;
                case LEGACY_RECIPIENT_VALUES.EMAIL2:
                case LIQUID_RECIPIENT_VALUES.EMAIL2:
                    return isSpa ? this.$t('unlayerBuilder.emailRecipient.label.personal') : this.$t('unlayerBuilder.emailRecipient.label.emailAddress2');
                case LEGACY_RECIPIENT_VALUES.EMAIL3:
                case LIQUID_RECIPIENT_VALUES.EMAIL3:
                    return isSpa ? this.$t('unlayerBuilder.emailRecipient.label.other') : this.$t('unlayerBuilder.emailRecipient.label.emailAddress3');
                default:
                    return defaultLabel;
                }
            }

            if (this.customEmailAddressRegex.test(this.toAddress)) {
                const [, name,, emailAddress] = this.customEmailAddressRegex.exec(this.toAddress);

                return name ? `${name} <${emailAddress}>` : `${emailAddress}`;
            }

            return defaultLabel;
        },
    },

    methods: {
        cancel() {
            this.hasError = false;
            this.syncData();
            this.$emit('cancel');
        },

        save() {
            this.hasError = !this.validate(this.toAddress);

            if (this.hasError) {
                this.submitted = true;
            } else {
                this.$emit('save', {
                    toAddress: this.toAddress,
                });
            }
        },

        isDefaultRecipientValue(toAddress) {
            const isLegacyMergeField = Object.values(LEGACY_RECIPIENT_VALUES).includes(toAddress);
            const isLiquidMergeField = Object.values(LIQUID_RECIPIENT_VALUES).includes(toAddress);

            return isLiquidMergeField || isLegacyMergeField;
        },

        validate(toAddr) {
            const isDefault = Object.values(this.recipientValues).includes(toAddr);
            const isCustom = this.customEmailAddressRegex.test(toAddr);

            return isDefault || isCustom;
        },

        syncData() {
            const isValid = this.validate(this.savedToAddress);

            if (!isValid) {
                this.toAddress = this.recipientValues.EMAIL;

                this.$emit('save', {
                    toAddress: this.toAddress,
                });
            } else {
                this.toAddress = this.savedToAddress;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.input-fields {
    --mergable-input-margin-bottom: #{$spacing-200};
}

.action-buttons {
    display: flex;
    align-items: flex-start;
}
</style>
